import { zodResolver } from '@hookform/resolvers/zod'
import { AnimatePresence, motion } from 'framer-motion'
import { TriangleAlertIcon } from 'lucide-react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { FormProvider } from '@/shared/components/HookForm'
import { ampli } from '@/shared/lib/ampli'

import { useAssistantConversation } from '../lib/useAssistantConversation'
import ChatInput from './ChatInput'

const MassageSchema = z.object({
	message: z.string().min(1),
})
export default function ChatFooter() {
	const { t } = useTranslation()

	const { isBusy, sendMessage } = useAssistantConversation()
	const methods = useForm<{
		message: string
	}>({
		resolver: zodResolver(MassageSchema),
		defaultValues: {
			message: '',
		},
	})
	const handleSubmit = methods.handleSubmit(({ message }) => {
		sendMessage(message)
		ampli.aiChatSentUserMessage()
		methods.reset({ message: '' })
	})

	return (
		<FormProvider
			methods={methods}
			onSubmit={handleSubmit}
			className='flex w-full flex-col items-start gap-8 px-3'
		>
			<div className='relative w-full pb-3'>
				<div className='relative mx-auto flex w-full max-w-3xl flex-col items-start gap-2'>
					<AnimatePresence>
						{isBusy && (
							<motion.div
								initial={{ opacity: 0, y: 10 }}
								animate={{ opacity: 1, y: 0 }}
								exit={{ opacity: 0, y: 10 }}
								transition={{ type: 'spring', stiffness: 300, damping: 20 }}
								className='absolute -top-6 left-0 pl-0'
							>
								<p className='shimmer-text mr-auto h-4 text-sm text-black/70 dark:text-white/70'>
									{t('ai.assistant.thinking', {
										defaultValue: 'AI is thinking...',
									})}
								</p>
							</motion.div>
						)}
					</AnimatePresence>
					<Controller
						control={methods.control}
						name='message'
						render={({ field }) => (
							<ChatInput onSubmit={handleSubmit} disabled={isBusy} {...field} />
						)}
					/>
					<div className='flex w-full items-center justify-center gap-2 text-xs text-muted-foreground'>
						<div className='flex items-center gap-1'>
							<TriangleAlertIcon className='h-3 w-3' />
							<span>
								{t('ai.assistant.warning', {
									defaultValue: 'AI can make mistakes, use with caution.',
								})}
							</span>
						</div>
					</div>
				</div>
				<style>{`
                .shimmer-text {
                    background: linear-gradient(
                        90deg,
                        var(--color-gray-400) 0%,
                        var(--color-gray-400) 40%,
                        var(--color-gray-200) 50%,
                        var(--color-gray-400) 60%,
                        var(--color-gray-400) 100%
                    );
                    background-size: 200% 100%;
                    -webkit-background-clip: text;
                    background-clip: text;
                    color: transparent;
                    animation: shimmer 2.5s infinite linear;
                }

                 
                @keyframes shimmer {
                    0% { background-position: 100% 0; }
                    100% { background-position: -100% 0; }
                }
            `}</style>
			</div>
		</FormProvider>
	)
}
